/**
 * Attention spans for the telemetry `attention` group (P2 design §4.4): how long each
 * surface (`[data-surface]`, lib/telemetry/registry) was visible, exposed while
 * the user was present, and attended.
 *
 * - One presence clock per tab. Presence needs the tab visible and focused (the seam) and
 *   recent activity. Activity listeners (services/p2Telemetry.js) only call `activity()`,
 *   which in the common case writes one timestamp. Presence is credited per gap between
 *   activity timestamps with the window `W` and cap of the tab's state:
 *     ordinary    W 60 s   a gap ≤ W in full, a longer one only the 5 s grace
 *     reading     W 120 s  as ordinary, capped by the expected reading time R of the
 *                          report / brief text not yet credited (230 words per minute)
 *     streaming   W 120 s  as ordinary (a run in progress on the attended chat / timeline /
 *                          forecast-manager row / progress card)
 *   A gap is split at every focus, visibility, attribution or state change; each part is
 *   judged by its own state, and a hidden or unfocused part credits nothing.
 * - Surfaces are found by attribute (a MutationObserver on the root) and watched by an
 *   IntersectionObserver: visible when at least half of it is in view or it covers at least
 *   half of the viewport. Surfaces outside an open modal surface are paused.
 * - Exactly one attended surface: the innermost surface under the pointer or holding the last
 *   interaction / keyboard focus; inside a report, the section covering most of the
 *   viewport. Fallbacks, so the tracker's own blind spots do not read as unattributed time:
 *     - the last target left the DOM (a modal closed under a still pointer): the element now
 *       under the last pointer position (`pointerAt`) stands in for it;
 *     - a modal surface is open and the target is behind it (the rail button that opened it),
 *       or there is no target yet: the topmost open modal is attended.
 *   Presence with no attended surface goes to the `unattributed` pseudo-surface; its parts
 *   carry `reason` (`UNATTRIBUTED_REASONS`) and, for `outside`, `region`: the nearest
 *   `data-ui` / `data-testid` id around the target (ids only, never text). A change of reason
 *   or region emits the pending unattributed part first, so each part has one reason.
 * - Spans per (surface, key): `visible_ms`, `exposed_ms` (visible while present) and
 *   `attended_ms`, emitted as `attention.span` parts carrying deltas — every
 *   `CHECKPOINT_MS` for spans with a non-zero delta, and a `final` part when the surface
 *   leaves view, its key changes, the tab hides or the visit ends. A checkpoint credits the
 *   open gap conservatively (only the grace); a later part adds the rest, so corrections are
 *   only additive. A span whose three totals all stay under `MIN_SPAN_MS` is dropped.
 * - Visible-only parts (no exposed or attended delta) are emitted only for top-level surfaces,
 *   so a surface in a background tab on a second monitor still counts visible time. A nested
 *   surface (its nearest ancestor is another surface: `evidence.card`, `report.section`, …)
 *   emits a part only when the part carries exposed or attended time: its visible-only time is
 *   carried into its next emitted part (or its final part, once it has emitted at all), and is
 *   dropped with the span when it never had exposed or attended time.
 * - A boxless surface (registry `boxless`: a timeline lane or branch, whose parts are all
 *   absolutely positioned, so its own element has no area) is visible while its parent is.
 * - `attention.presence {presence_ms, part}` carries the presence clock's delta at each
 *   checkpoint (when non-zero) and when the tab hides, so visit active time never depends on
 *   a surface being attended.
 *
 * Every input that tests need to control is injected: the monotonic clock (`now`, default
 * `performance.now`), the focus / visibility seam, the observers and timers.
 */
import { SURFACES } from "../lib/telemetry/registry";

export const CHECKPOINT_MS = 30_000;
export const GRACE_MS = 5_000;
export const WINDOW_MS = Object.freeze({ ordinary: 60_000, reading: 120_000, streaming: 120_000 });
export const READING_WORDS_PER_MIN = 230;
export const MIN_SPAN_MS = 1_000;
export const UNATTRIBUTED = "unattributed";

const SURFACE_ATTR = "data-surface";
const KEY_ATTR = "data-surface-key";
const READING = new Set(["report", "brief"]);
/**
 * Where the attended report section is picked: a report's root and the Mirabilis brief, whose
 * sections and chapters are `report.section`s too.
 */
const SECTION_ROOTS = new Set(["report", "brief"]);
const STREAMING = new Set([
  "chat",
  "timeline",
  "timeline.axis",
  "timeline.canvas",
  "timeline.lane",
  "timeline.branch",
  "forecast-manager.row",
  "run-status",
]);
/** Surfaces that cover the app while open (registry `modal`): everything outside them is paused. */
export const MODAL_SURFACES = new Set(
  Object.entries(SURFACES)
    .filter(([, s]) => s.modal === true)
    .map(([name]) => name),
);
const MODALS = MODAL_SURFACES;
/** Surfaces with no box of their own (registry `boxless`): visible while their parent is. */
const BOXLESS = new Set(
  Object.entries(SURFACES)
    .filter(([, s]) => s.boxless === true)
    .map(([name]) => name),
);
/**
 * Why presence went to `unattributed`: no interaction target yet, the target left the DOM
 * with no pointer position to re-resolve it, the target's surfaces are not attendable (under
 * half visible, inert or hidden), or the target is in no surface at all.
 */
export const UNATTRIBUTED_REASONS = Object.freeze(["no-target", "detached", "hidden", "outside"]);
/** An id the payload may carry as `region` (the server's key shape). */
const REGION_RE = /^[A-Za-z0-9._:-]{1,64}$/;
const IO_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];
const RESCAN_MS = 200;
/** Most frequent geometry refresh on scroll (see `scrolled`). */
const SCROLL_GEOMETRY_MS = 250;

/** Words in `text`, for the reading-time cap. */
export function wordCount(text) {
  if (typeof text !== "string") return 0;
  const m = text.match(/\S+/g);
  return m ? m.length : 0;
}

/** Expected reading time of `words` words, in ms. */
export const readingMs = (words) => (words / READING_WORDS_PER_MIN) * 60_000;

function forecastScope(name) {
  return SURFACES[name]?.forecastScoped ?? "no";
}

/**
 * A span's identity: surface, key and the forecast its time counts toward — a parent-scoped
 * surface (a report section) keeps its key when its report changes forecast, and must not
 * carry its time over.
 */
const spanKey = (surface, key, forecastId) =>
  `${surface}\u0000${key ?? ""}\u0000${forecastId ?? ""}`;

/**
 * Create a tracker. `record` receives `attention.span` / `attention.presence` events;
 * `onActivity` is called for every activity the presence clock accepts (visits);
 * `onAttended(surface, forecastId)` whenever attended time is credited to a surface (the intent
 * emitter's `forecast.result_seen`);
 * `getContext()` returns `{streamingIds}` — the forecast ids with a run in progress.
 */
export function createAttentionTracker({
  record,
  now = () => performance.now(),
  seam = {
    visible: () => typeof document === "undefined" || document.visibilityState !== "hidden",
    focused: () => typeof document === "undefined" || document.hasFocus(),
  },
  root = typeof document !== "undefined" ? document.body : null,
  IntersectionObserverImpl = typeof IntersectionObserver !== "undefined"
    ? IntersectionObserver
    : null,
  MutationObserverImpl = typeof MutationObserver !== "undefined" ? MutationObserver : null,
  setIntervalImpl = (fn, ms) => setInterval(fn, ms),
  clearIntervalImpl = (id) => clearInterval(id),
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl = (id) => clearTimeout(id),
  newId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `s-${Math.random().toString(36).slice(2)}`,
  onActivity = () => {},
  onAttended = null,
  getContext = () => ({ streamingIds: null }),
  checkpointMs = CHECKPOINT_MS,
  viewport = () =>
    typeof window !== "undefined" ? { w: window.innerWidth, h: window.innerHeight } : null,
  elementFromPoint = (x, y) =>
    typeof document !== "undefined" && typeof document.elementFromPoint === "function"
      ? document.elementFromPoint(x, y)
      : null,
} = {}) {
  let running = false;
  let io = null;
  let mo = null;
  let timer = null;
  let rescanTimer = null;
  let lastGeometryAt = -Infinity;

  /** Element → {surface, key, intersecting, visible, share} for every tracked surface. */
  const elements = new Map();
  /** spanKey → span. */
  const spans = new Map();

  // Presence clock. `docVisible` / `present` hold the seam's state as of the last settle, so
  // a settle after the seam changed still accrues the interval before it under the old one.
  let docVisible = false;
  let present = false;
  let away = true;
  let lastAct = 0;
  let creditedTo = 0;
  let presencePending = 0;
  let presencePart = 0;
  let lastSettle = 0;

  // Attribution.
  let attendTarget = null;
  let attendedSpan = null;
  /** The element `attendedSpan` was resolved from. */
  let attendedEl = null;
  /** The reading surface (report / brief) holding attention, and its cap. */
  let readingEl = null;
  let reading = null;
  /** spanKey of a reading surface → {total, credited} (ms): the cap R outlives its spans. */
  const readCaps = new Map();
  let mode = "ordinary";
  let paused = null; // the open modal element, or null
  /** The last pointer position in viewport coordinates (`pointerAt`), or null. */
  let pointer = null;
  /** Why the current presence is unattributed: `{reason, region}`. */
  let unattributedWhy = { reason: "no-target", region: null };

  function newSpan(surface, key, forecastId, nested) {
    return {
      id: newId(),
      surface,
      key: key ?? null,
      forecastId: forecastId ?? null,
      nested: Boolean(nested),
      part: 0,
      emitted: false,
      total: { v: 0, e: 0, a: 0 },
      acc: { v: 0, e: 0, a: 0 },
      visible: false,
      /** Unattributed only: attended time set aside under the diagnostic it accrued under. */
      carried: [],
    };
  }

  function spanFor(surface, key, forecastId, nested = false) {
    const k = spanKey(surface, key, forecastId);
    let s = spans.get(k);
    if (!s) {
      s = newSpan(surface, key, forecastId, nested);
      spans.set(k, s);
    }
    return s;
  }

  function add(span, field, ms) {
    span.acc[field] += ms;
    span.total[field] += ms;
  }

  /** A nested surface's visible time alone never keeps its span. */
  const passes = (s) =>
    (!s.nested && s.total.v >= MIN_SPAN_MS) || s.total.e >= MIN_SPAN_MS || s.total.a >= MIN_SPAN_MS;

  function emitPart(s, final) {
    let carriedOut = false;
    for (const c of s.carried.splice(0)) {
      const ca = Math.round(c.a);
      s.acc.a += c.a - ca;
      if (ca <= 0) continue;
      carriedOut = true;
      s.part += 1;
      s.emitted = true;
      record({
        event: "attention.span",
        span_id: s.id,
        surface: s.surface,
        key: s.key,
        forecast_id: s.forecastId,
        visible_ms: 0,
        exposed_ms: 0,
        attended_ms: ca,
        part: s.part,
        final: false,
        ...diagnosticOf(c.why),
      });
    }
    if (carriedOut && !final && !hasDelta(s)) return;
    const v = Math.round(s.acc.v);
    const e = Math.round(s.acc.e);
    const a = Math.round(s.acc.a);
    s.acc.v -= v;
    s.acc.e -= e;
    s.acc.a -= a;
    s.part += 1;
    s.emitted = true;
    record({
      event: "attention.span",
      span_id: s.id,
      surface: s.surface,
      key: s.key,
      forecast_id: s.forecastId,
      visible_ms: v,
      exposed_ms: e,
      attended_ms: a,
      part: s.part,
      final,
      ...(s.surface === UNATTRIBUTED ? diagnosticOf(unattributedWhy) : {}),
    });
  }

  function diagnosticOf({ reason, region }) {
    return region ? { reason, region } : { reason };
  }

  function hasDelta(s) {
    return Math.round(s.acc.v) > 0 || Math.round(s.acc.e) > 0 || Math.round(s.acc.a) > 0;
  }

  const hasCarried = (s) => s.carried.some((c) => Math.round(c.a) > 0);

  /** A checkpoint part of a nested span needs exposed or attended time (visible is carried). */
  function checkpointWorthy(s) {
    if (!s.nested) return hasDelta(s) || hasCarried(s);
    return Math.round(s.acc.e) > 0 || Math.round(s.acc.a) > 0;
  }

  function closeSpan(k, s) {
    spans.delete(k);
    if (s === attendedSpan) attendedSpan = null;
    if (s.emitted || passes(s)) emitPart(s, true);
  }

  function emitPresence() {
    const ms = Math.round(presencePending);
    if (ms <= 0) return;
    presencePending -= ms;
    presencePart += 1;
    record({ event: "attention.presence", presence_ms: ms, part: presencePart });
  }

  // ── presence clock ────────────────────────────────────────────────────────

  const windowMs = () => WINDOW_MS[mode] ?? WINDOW_MS.ordinary;

  /** Credit presence up to `target` to the current attribution. */
  function creditTo(target) {
    let d = target - creditedTo;
    if (d <= 0) return;
    creditedTo = target;
    if (mode === "reading" && reading) {
      d = Math.min(d, Math.max(0, reading.total - reading.credited));
      reading.credited += d;
    }
    if (d <= 0) return;
    presencePending += d;
    const target_ = attendedSpan ?? spanFor(UNATTRIBUTED, null, null);
    add(target_, "a", d);
    if (onAttended && attendedSpan) {
      try {
        onAttended(attendedSpan.surface, attendedSpan.forecastId);
      } catch {
        /* never surface */
      }
    }
    for (const s of spans.values()) if (s.visible) add(s, "e", d);
  }

  /** Accrue visible time and presence up to `t` with the state as it was since the last call. */
  function settle(t, kind) {
    // Text that streamed into the report since (a text-only update is no structural mutation)
    // raises the cap before the interval is credited against it.
    if (mode === "reading") refreshReadingCap();
    if (docVisible) {
      const dv = t - lastSettle;
      if (dv > 0) for (const s of spans.values()) if (s.visible) add(s, "v", dv);
    }
    lastSettle = t;
    if (!present || away) return;
    const tail = t - lastAct;
    if (tail > windowMs()) {
      creditTo(lastAct + GRACE_MS);
      away = true;
    } else if (kind === "checkpoint") {
      creditTo(Math.min(t, lastAct + GRACE_MS));
    } else {
      // A split: the segment so far is judged and credited by its own state, and the next one
      // is measured from here, under the state that follows.
      creditTo(t);
      lastAct = t;
    }
  }

  // ── attribution ───────────────────────────────────────────────────────────

  function insideInert(el) {
    return Boolean(el.closest?.("[inert]"));
  }

  /**
   * Whether a tracked surface counts as visible. A boxless surface (a group of absolutely
   * positioned parts, such as a timeline lane) has no area for the observer to measure, so it
   * takes its parent surface's visibility.
   */
  function shown(el, info) {
    if (!BOXLESS.has(info.surface)) return info.visible;
    const parent = el.parentElement?.closest?.(`[${SURFACE_ATTR}]`) ?? null;
    const parentInfo = parent ? elements.get(parent) : null;
    return parentInfo ? shown(parent, parentInfo) : false;
  }

  function spanIdentity(el) {
    const surface = el.getAttribute(SURFACE_ATTR);
    const key = el.getAttribute(KEY_ATTR) || null;
    const scope = forecastScope(surface);
    const parent = el.parentElement?.closest?.(`[${SURFACE_ATTR}]`) ?? null;
    let forecastId = null;
    if (scope === "yes" || scope === "when-set") forecastId = key;
    else if (scope === "parent" && parent) forecastId = spanIdentity(parent).forecastId;
    return { surface, key, forecastId, nested: parent !== null };
  }

  /** The modal's stacking order: its own or its backdrop's z-index. */
  function zOf(el) {
    try {
      const host = el.closest?.(".modal-shell-backdrop") ?? el;
      return Number.parseInt(getComputedStyle(host).zIndex, 10) || 0;
    } catch {
      return 0;
    }
  }

  /**
   * A modal hidden with CSS `visibility` (the bug report while the screen-capture picker is up)
   * keeps its geometry, so the observer still reports it intersecting.
   */
  function cssHidden(el) {
    try {
      return (
        typeof el.checkVisibility === "function" &&
        !el.checkVisibility({ visibilityProperty: true })
      );
    } catch {
      return false;
    }
  }

  /** The topmost open modal surface (highest z-index, then latest in document order). */
  function findModal() {
    let top = null;
    for (const [el, info] of elements) {
      if (!MODALS.has(info.surface) || !info.intersecting || insideInert(el) || cssHidden(el)) {
        continue;
      }
      if (top === null) {
        top = el;
        continue;
      }
      const dz = zOf(el) - zOf(top);
      const later =
        typeof top.compareDocumentPosition === "function" &&
        (top.compareDocumentPosition(el) & 4) !== 0; // DOCUMENT_POSITION_FOLLOWING
      if (dz > 0 || (dz === 0 && later)) top = el;
    }
    return top;
  }

  const pausedEl = (el) => paused !== null && paused !== el && !paused.contains(el);

  function underPointer() {
    if (!pointer) return null;
    try {
      return elementFromPoint(pointer.x, pointer.y) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * The element attention is resolved from: the last interaction target, or — when it has left
   * the DOM (a modal closed under a still pointer) or there is none yet — the element under
   * the last pointer position, which then becomes the target. `lost` is why there is none.
   */
  function currentTarget() {
    const detached = attendTarget !== null && attendTarget.isConnected === false;
    if (attendTarget === null || detached) {
      const under = underPointer();
      if (under) {
        attendTarget = under;
        return { target: under, lost: null };
      }
      return { target: null, lost: detached ? "detached" : "no-target" };
    }
    return { target: attendTarget, lost: null };
  }

  /** The nearest id-shaped `data-ui` / `data-testid` around `el`, or null (ids, never text). */
  function regionOf(el) {
    for (let n = el; n; n = n.parentElement) {
      for (const attr of ["data-ui", "data-testid"]) {
        const id = n.getAttribute?.(attr);
        if (id && REGION_RE.test(id)) return id;
      }
    }
    return null;
  }

  /** The attended surface element, or null. */
  function attendedElement() {
    return resolveAttention().el;
  }

  /**
   * `{el, reason, region}`: the attended surface element, or null with why (see
   * `UNATTRIBUTED_REASONS`).
   */
  function resolveAttention() {
    const { target, lost } = currentTarget();
    let el = target?.closest?.(`[${SURFACE_ATTR}]`) ?? null;
    const inSurface = el !== null;
    while (el) {
      const info = elements.get(el);
      if (
        info &&
        shown(el, info) &&
        !insideInert(el) &&
        !pausedEl(el) &&
        !(MODALS.has(info.surface) && cssHidden(el))
      ) {
        break;
      }
      el = el.parentElement?.closest(`[${SURFACE_ATTR}]`) ?? null;
    }
    // Behind an open modal, or no target yet: the user is looking at the modal.
    if (!el && paused !== null && elements.has(paused)) el = paused;
    if (!el) {
      const reason = lost ?? (inSurface ? "hidden" : "outside");
      return { el: null, reason, region: reason === "outside" ? regionOf(target) : null };
    }
    const name = elements.get(el).surface;
    if (!SECTION_ROOTS.has(name) && name !== "report.section") {
      return { el, reason: null, region: null };
    }
    // Inside a report or a brief: the section covering most of the viewport.
    const report = SECTION_ROOTS.has(name) ? el : el.parentElement?.closest(`[${SURFACE_ATTR}]`);
    let best = null;
    let bestShare = 0;
    for (const [sec, info] of elements) {
      if (info.surface !== "report.section" || !info.visible) continue;
      if (report && !report.contains(sec)) continue;
      if (info.share > bestShare) {
        best = sec;
        bestShare = info.share;
      }
    }
    return { el: best ?? el, reason: null, region: null };
  }

  /** Unattributed, and the attribution or its reason / region would now differ. */
  function unattributedMoved() {
    const next = resolveAttention();
    return (
      next.el !== null ||
      next.reason !== unattributedWhy.reason ||
      next.region !== unattributedWhy.region
    );
  }

  /**
   * Presence is unattributed for another reason now: the part accrued under the old one is
   * emitted first (a span that has not passed `MIN_SPAN_MS` yet carries it to the new one).
   */
  function noteUnattributed(reason, region) {
    if (unattributedWhy.reason === reason && unattributedWhy.region === region) return;
    const s = spans.get(spanKey(UNATTRIBUTED, null, null));
    if (s && s.acc.a > 0) {
      if (s.emitted || passes(s)) emitPart(s, false);
      else {
        // Not kept yet (under MIN_SPAN_MS): its time stays with the reason it accrued under.
        s.carried.push({ why: unattributedWhy, a: s.acc.a });
        s.acc.a = 0;
      }
    }
    unattributedWhy = { reason, region };
  }

  function readingElement(el) {
    let cur = el;
    while (cur) {
      if (READING.has(cur.getAttribute(SURFACE_ATTR))) return cur;
      cur = cur.parentElement?.closest(`[${SURFACE_ATTR}]`) ?? null;
    }
    return null;
  }

  function modeFor(el, span) {
    if (!el) return "ordinary";
    if (readingElement(el)) return "reading";
    const streaming = getContext()?.streamingIds;
    if (!STREAMING.has(span.surface) || !streaming?.has) return "ordinary";
    // A compared lane streams with its own forecast (its key: the trunk run id, which is the
    // forecast's id), not the one its time counts toward.
    const lane = el.closest?.(`[${SURFACE_ATTR}="timeline.lane"]`);
    for (const id of [span.forecastId, lane?.getAttribute(KEY_ATTR)]) {
      if (id && streaming.has(id)) return "streaming";
    }
    return "ordinary";
  }

  /**
   * The cap grows with the text (a report that is still streaming in): raised before any
   * interval is credited against it.
   */
  function refreshReadingCap() {
    if (readingEl && reading) reading.total = Math.max(reading.total, readTotalOf(readingEl));
  }

  /** Expected reading time of a reading surface's text, in ms. */
  function readTotalOf(el) {
    try {
      return readingMs(wordCount(el.textContent || ""));
    } catch {
      return 0;
    }
  }

  /** Recompute visible spans, the attended span and the mode; open / close spans to match. */
  function recompute() {
    paused = findModal();
    const visibleKeys = new Set();
    const idOf = new Map();
    for (const [el, info] of elements) {
      if (!shown(el, info) || !docVisible || insideInert(el) || pausedEl(el)) continue;
      if (MODALS.has(info.surface) && cssHidden(el)) continue;
      const id = spanIdentity(el);
      const k = spanKey(id.surface, id.key, id.forecastId);
      visibleKeys.add(k);
      idOf.set(k, id);
    }
    let attEl = null;
    if (present) {
      const resolved = resolveAttention();
      attEl = resolved.el;
      if (!attEl) noteUnattributed(resolved.reason, resolved.region);
    }
    attendedEl = attEl;
    let attKey = null;
    let attId = null;
    if (attEl) {
      attId = spanIdentity(attEl);
      attKey = spanKey(attId.surface, attId.key, attId.forecastId);
    }
    // Close spans that are neither visible nor attended any more.
    for (const [k, s] of [...spans]) {
      if (s.surface === UNATTRIBUTED) continue;
      if (!visibleKeys.has(k) && k !== attKey) closeSpan(k, s);
    }
    for (const k of visibleKeys) {
      const id = idOf.get(k);
      spanFor(id.surface, id.key, id.forecastId, id.nested).visible = true;
    }
    for (const [k, s] of spans) if (!visibleKeys.has(k)) s.visible = false;
    attendedSpan = attId ? spanFor(attId.surface, attId.key, attId.forecastId, attId.nested) : null;
    // The reading cap belongs to the report / brief: its sections share it.
    const readEl = attEl ? readingElement(attEl) : null;
    if (readEl) {
      const rid = spanIdentity(readEl);
      const rk = spanKey(rid.surface, rid.key, rid.forecastId);
      let cap = readCaps.get(rk);
      if (!cap) {
        cap = { total: readTotalOf(readEl), credited: 0 };
        readCaps.set(rk, cap);
      } else if (readEl !== readingEl) {
        cap.total = Math.max(cap.total, readTotalOf(readEl));
      }
      reading = cap;
    } else {
      reading = null;
    }
    readingEl = readEl;
    mode = modeFor(attEl, attendedSpan);
  }

  /**
   * Shares changed but no visibility: split only if a different report section now covers
   * most of the viewport (the settle uses spans and attribution, which shares do not touch).
   */
  function sectionMoved(t) {
    if (!present || attendedEl === null) return;
    if (attendedElement() !== attendedEl) change(t);
  }

  /** A state change at `t`: settle under the old state, then recompute. */
  function change(t = now()) {
    if (!running) return;
    settle(t, "split");
    recompute();
  }

  // ── observers ─────────────────────────────────────────────────────────────

  /**
   * Observer entries. Only a change that can move a span (visibility, or which report section
   * covers most of the viewport) splits the gap: a split credits the open gap in full up to
   * `W`, so churn that changes nothing must not split.
   */
  function onIntersect(entries) {
    const updates = [];
    let changed = false;
    for (const entry of entries) {
      const info = elements.get(entry.target);
      if (!info) continue;
      const r = entry.intersectionRect;
      const rb = entry.rootBounds;
      const area = r ? r.width * r.height : 0;
      const viewport = rb && rb.width * rb.height > 0 ? rb.width * rb.height : 0;
      const share = viewport > 0 ? area / viewport : 0;
      const intersecting = Boolean(entry.isIntersecting) && area > 0;
      const visible = intersecting && (entry.intersectionRatio >= 0.5 || share >= 0.5);
      if (
        visible !== info.visible ||
        // An intersecting sliver matters only to modal detection, a share only to choosing the
        // visible report section.
        (MODALS.has(info.surface) && intersecting !== info.intersecting)
      ) {
        changed = true;
      }
      updates.push([info, share, intersecting, visible]);
    }
    if (changed) settle(now(), "split");
    for (const [info, share, intersecting, visible] of updates) {
      info.share = share;
      info.intersecting = intersecting;
      info.visible = visible;
    }
    if (changed) recompute();
    else sectionMoved(now());
  }

  /**
   * An element taller (or wider) than four viewports can cross the "covers half the viewport"
   * line without its own intersection ratio crossing an observer threshold, so on scroll the
   * share of every intersecting, partly visible surface is re-measured.
   */
  function measureGeometry() {
    const vp = viewport();
    if (!vp || !(vp.w > 0 && vp.h > 0)) return [];
    const area = vp.w * vp.h;
    const out = [];
    for (const [el, info] of elements) {
      if (!info.intersecting || !el.getBoundingClientRect) continue;
      const r = el.getBoundingClientRect();
      const w = Math.max(0, Math.min(r.right, vp.w) - Math.max(r.left, 0));
      const h = Math.max(0, Math.min(r.bottom, vp.h) - Math.max(r.top, 0));
      const own = r.width * r.height;
      const share = (w * h) / area;
      const visible = w * h > 0 && ((own > 0 && (w * h) / own >= 0.5) || share >= 0.5);
      out.push([info, share, visible]);
    }
    return out;
  }

  function track(el) {
    if (elements.has(el)) return;
    elements.set(el, {
      surface: el.getAttribute(SURFACE_ATTR),
      intersecting: false,
      visible: false,
      share: 0,
    });
    io?.observe(el);
  }

  function untrack(el) {
    if (!elements.has(el)) return;
    elements.delete(el);
    io?.unobserve(el);
  }

  /**
   * Re-read the tracked surfaces. A newly found one is not visible until the observer says so,
   * so only losing one that moves a span splits the gap (unless `force`). A boxless surface
   * gets no observer callback and, once removed, no parent to inherit from: adding or removing
   * one always moves a span.
   */
  function rescan(force = false) {
    rescanTimer = null;
    if (!running || !root) return;
    const found = new Set(root.querySelectorAll?.(`[${SURFACE_ATTR}]`) ?? []);
    const removed = [...elements.keys()].filter((el) => !found.has(el));
    const boxlessAdded = [...found].some(
      (el) => !elements.has(el) && BOXLESS.has(el.getAttribute(SURFACE_ATTR)),
    );
    const lost = removed.some((el) => {
      const info = elements.get(el);
      return (
        el === attendedEl ||
        BOXLESS.has(info?.surface) ||
        (info && shown(el, info)) ||
        (MODALS.has(info?.surface) && info?.intersecting)
      );
    });
    const moved = lost || boxlessAdded || force;
    if (moved) settle(now(), "split");
    for (const el of removed) untrack(el);
    for (const el of found) track(el);
    if (moved) recompute();
  }

  function onMutations(records) {
    let structural = false;
    let attrs = false;
    for (const m of records) {
      if (m.type === "childList") structural = true;
      else if (m.type === "attributes") {
        if (m.attributeName === SURFACE_ATTR) structural = true;
        else attrs = true;
      }
    }
    if (structural && rescanTimer === null) rescanTimer = setTimeoutImpl(rescan, RESCAN_MS);
    if (attrs) {
      for (const [el, info] of elements) info.surface = el.getAttribute(SURFACE_ATTR);
      change();
    }
  }

  // ── public ────────────────────────────────────────────────────────────────

  function checkpoint() {
    if (!running) return;
    const t = now();
    settle(t, "checkpoint");
    for (const s of spans.values()) {
      if (checkpointWorthy(s) && (s.emitted || passes(s))) emitPart(s, false);
    }
    emitPresence();
  }

  /** Close every span with a final part and send the presence delta (hide, visit end, stop). */
  function closeAll() {
    if (!running) return;
    settle(now(), "split");
    for (const [k, s] of [...spans]) closeSpan(k, s);
    emitPresence();
  }

  /** Focus or visibility may have changed: re-read the seam. */
  function notify() {
    if (!running) return;
    const t = now();
    const next = Boolean(seam.visible() && seam.focused());
    if (!seam.visible()) {
      // Hidden: every span ends here.
      settle(t, "split");
      docVisible = false;
      present = false;
      for (const [k, s] of [...spans]) closeSpan(k, s);
      emitPresence();
      recompute();
      return;
    }
    if (next === present && docVisible) {
      change(t);
      return;
    }
    settle(t, "split");
    docVisible = true;
    present = next;
    if (present) {
      // Coming back counts as activity.
      away = false;
      lastAct = t;
      creditedTo = t;
      onActivity();
    }
    recompute();
  }

  return {
    start() {
      if (running) return;
      running = true;
      const t = now();
      lastSettle = t;
      docVisible = Boolean(seam.visible());
      present = Boolean(docVisible && seam.focused());
      away = true;
      try {
        if (IntersectionObserverImpl) {
          io = new IntersectionObserverImpl(onIntersect, { threshold: IO_THRESHOLDS });
        }
        if (MutationObserverImpl && root) {
          mo = new MutationObserverImpl(onMutations);
          mo.observe(root, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: [SURFACE_ATTR, KEY_ATTR, "inert"],
          });
        }
        rescanTimer = null;
        rescan(true);
        timer = setIntervalImpl(checkpoint, checkpointMs);
      } catch {
        /* attention is best-effort */
      }
    },
    stop() {
      if (!running) return;
      closeAll();
      running = false;
      try {
        io?.disconnect();
        mo?.disconnect();
      } catch {
        /* best effort */
      }
      io = null;
      mo = null;
      if (timer !== null) clearIntervalImpl(timer);
      if (rescanTimer !== null) clearTimeoutImpl(rescanTimer);
      timer = null;
      rescanTimer = null;
      elements.clear();
      spans.clear();
      readCaps.clear();
      attendedSpan = null;
      attendedEl = null;
      attendTarget = null;
      pointer = null;
      unattributedWhy = { reason: "no-target", region: null };
      readingEl = null;
      reading = null;
    },
    /**
     * The user did something at monotonic time `t`. `target`, when given, is the element
     * an interaction landed on (pointer over / down, focus, touch): it may move attention.
     */
    activity(t = now(), target = null) {
      if (!running || !present) return;
      if (away) {
        away = false;
        lastAct = t;
        creditedTo = t;
        onActivity();
      } else if (t - lastAct <= windowMs()) {
        lastAct = t;
        onActivity();
      } else {
        // The user had left: the gap credits only the grace; they are back now.
        creditTo(lastAct + GRACE_MS);
        creditedTo = t;
        lastAct = t;
        onActivity();
      }
      if (target) this.attend(t, target);
    },
    /** The pointer or focus is now on `target`: it may move attention (not activity itself). */
    attend(t = now(), target = null) {
      if (!running || !target || target === attendTarget) return;
      const before = attendTarget?.closest?.(`[${SURFACE_ATTR}]`) ?? null;
      attendTarget = target;
      const after = target.closest?.(`[${SURFACE_ATTR}]`) ?? null;
      if (before !== after) change(t);
      else if (after === null && present && attendedEl === null && unattributedMoved()) change(t);
    },
    /**
     * The pointer is at viewport `(x, y)`: kept to re-resolve attention when the last target
     * leaves the DOM (not activity, and moves no attention by itself).
     */
    pointerAt(x, y) {
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      pointer = { x, y };
      // With no live target, the element under the pointer may now be attended.
      const live = attendTarget !== null && attendTarget.isConnected !== false;
      if (running && present && !live && attendedEl === null && unattributedMoved()) change();
    },
    checkpoint,
    closeAll,
    notify,
    /** The page or a container scrolled, or the viewport resized: re-measure (throttled). */
    scrolled(t = now()) {
      if (!running || t - lastGeometryAt < SCROLL_GEOMETRY_MS) return;
      lastGeometryAt = t;
      // Split only when a span can move (see onIntersect): a scroll is not activity, and a
      // split credits the open gap.
      const measured = measureGeometry();
      const changed = measured.some(([info, , visible]) => visible !== info.visible);
      if (changed) settle(t, "split");
      for (const [info, share, visible] of measured) {
        info.share = share;
        info.visible = visible;
      }
      if (changed) recompute();
      else sectionMoved(t);
    },
    /** A new visit: reading credit (the cap R) starts over. Call after `closeAll`. */
    newVisit() {
      readCaps.clear();
      reading = null;
      readingEl = null;
    },
    /** The tracked surfaces changed outside a mutation (tests, a visit change). */
    refresh() {
      if (!running) return;
      rescan(true);
    },
    /** Context (streaming runs) changed. */
    contextChanged() {
      if (!running) return;
      // Another forecast's run starting or ending moves no span: split only when the attended
      // surface's state actually changes.
      const el = present ? attendedElement() : null;
      if (modeFor(el, attendedSpan) !== mode) change();
    },
    get running() {
      return running;
    },
  };
}
